import React, { useMemo, useState } from "react";
import { useInput, Button, required } from "react-admin";
import PropTypes from "prop-types";
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

// Check if required() is in validate
const isRequired = (validate) => {
  if (!validate) return false;

  // Handle single function
  if (typeof validate === "function") {
    // Check if validate is the required function or its result
    if (validate === required || validate.toString().includes("required")) {
      return true;
    }
    // Check if invoking validate matches behavior of required()
    try {
      const result = validate(undefined);
      const requiredResult = required()(undefined);
      return result === requiredResult && result !== undefined;
    } catch (e) {
      return false;
    }
  }

  // Handle array of validators
  return (
    Array.isArray(validate) &&
    validate.some((v) => {
      if (v === required || v.toString().includes("required")) {
        return true;
      }
      try {
        const result = v(undefined);
        const requiredResult = required()(undefined);
        return result === requiredResult && result !== undefined;
      } catch (e) {
        return false;
      }
    })
  );
};

// Get the last day of a given month/year
const getLastDayOfMonth = (year, month) => {
  return new Date(year, month, 0).getDate();
};

// Generate month choices (01-12)
const monthChoices = Array.from({ length: 12 }, (_, i) => {
  const month = (i + 1).toString().padStart(2, "0");
  return { id: month, name: month };
});

// Generate year choices (current year to 10 years in future)
const generateYearChoices = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, i) => {
    const year = currentYear + i;
    return { id: year.toString(), name: year.toString() };
  });
};

const CustomDateInput = ({ source, label, validate, defaultDay, isValidFrom, isValidTo }) => {
  const { id, field, fieldState } = useInput({ source, validate });
  const [open, setOpen] = useState(false);
  const [tempDate, setTempDate] = useState(null);

  // Determine default date components (used only for initial modal values)
  const defaultDate = useMemo(() => {
    return { year: "", month: "", day: defaultDay || "01" };
  }, [defaultDay]);

  const [year, month, day] =
    typeof field.value === "string" && field.value.includes("-") ? field.value.split("-") : ["", "", ""];

  const handleChange = (type) => (event) => {
    const value = event.target.value;
    let newYear = tempDate?.year || defaultDate.year;
    let newMonth = tempDate?.month || defaultDate.month;
    let newDay = tempDate?.day || defaultDate.day;

    if (type === "year") newYear = value;
    else if (type === "month") newMonth = value;

    if (isValidTo && newYear && newMonth)
      newDay = getLastDayOfMonth(parseInt(newYear), parseInt(newMonth)).toString().padStart(2, "0");
    else if (isValidFrom) newDay = "01";
    setTempDate({ year: newYear, month: newMonth, day: newDay });
  };

  const handleDone = () => {
    if (tempDate && tempDate.year && tempDate.month)
      field.onChange(`${tempDate.year}-${tempDate.month}-${tempDate.day}`);
    setOpen(false);
  };

  const handleOpen = () => {
    setTempDate({
      year: year || defaultDate.year,
      month: month || defaultDate.month,
      day: day || defaultDay || "01",
    });
    setOpen(true);
  };

  // Format display value as mm/dd/yyyy
  const displayValue = year && month && day ? `${month}/${day}/${year}` : "mm/dd/yyyy";
  const showRequiredAsterisk = isRequired(validate);

  return (
    <React.Fragment>
      <TextField
        sx={{ input: { cursor: "default", letterSpacing: "0.5px" } }}
        id={id}
        label={
          <span>
            {label}
            {showRequiredAsterisk ? " *" : ""}
          </span>
        }
        value={displayValue}
        onClick={handleOpen}
        error={!!fieldState.error}
        helperText={fieldState.error ? `${label} is required` : ""}
        fullWidth
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <CalendarTodayIcon
                style={{
                  cursor: "default",
                  width: "15px",
                  display: "flex",
                  alignSelf: "baseline",
                  paddingBottom: "6px",
                  color: "black",
                }}
              />
              <span> </span>
            </InputAdornment>
          ),
        }}
      />
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Select Month and Year</DialogTitle>
        <DialogContent>
          <div style={{ display: "flex", gap: "1rem", width: "100%" }}>
            <Select
              label="Month"
              style={{ width: "50%" }}
              value={tempDate ? tempDate.month : ""}
              onChange={handleChange("month")}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select Month
              </MenuItem>
              {monthChoices.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
            <Select
              label="Year"
              style={{ width: "50%" }}
              value={tempDate ? tempDate.year : ""}
              onChange={handleChange("year")}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select Year
              </MenuItem>
              {generateYearChoices().map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} label="Cancel" />
          <Button onClick={handleDone} label="Done" />
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

CustomDateInput.propTypes = {
  source: PropTypes.string.isRequired,
  label: PropTypes.string,
  validate: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.func)]),
  defaultDay: PropTypes.string,
  isValidFrom: PropTypes.bool,
  isValidTo: PropTypes.bool,
};

export default CustomDateInput;
